import { forwardRef } from "react";
import { cn } from "@/lib/cn";
import { formatRelative } from "@/lib/format";
import { ChannelTypeBadge } from "./ChannelTypeBadge";
import { ConversationStateBadge } from "./ConversationStateBadge";
import type { Conversation } from "@/modules/chatbot/types";

/**
 * ConversationListItem — Inbox chap panel item.
 *
 * Token-aware (--conversation-row-*, --conversation-unread-*).
 *
 * UX:
 *  - Avatar (h-10 w-10) + channel-type ikon overlay (bottom-right)
 *  - Aktiv qator chap chetida 2px Aurora violet "rail"
 *  - Unread → violet glow dot + bold name + violet count chip
 *  - Hover → bg-surface
 *  - Last message preview "Siz: …" prefiks (outgoing)
 */

export interface ConversationListItemProps
  extends React.HTMLAttributes<HTMLButtonElement> {
  conversation: Conversation;
  active?: boolean;
}

export const ConversationListItem = forwardRef<
  HTMLButtonElement,
  ConversationListItemProps
>(({ conversation: c, active, className, onClick, ...props }, ref) => {
  const name = c.customerName || c.externalUsername || "Noma'lum";
  const initial = name.slice(0, 1).toUpperCase();
  const hasUnread = c.unreadCount > 0;

  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      aria-current={active ? "true" : undefined}
      aria-label={`Suhbat: ${name}${hasUnread ? `, ${c.unreadCount} o'qilmagan` : ""}`}
      className={cn(
        "group relative flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors",
        "border border-transparent rounded-lg",
        "hover:bg-[var(--conversation-row-bg-hover)]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-violet)]/50",
        active && "bg-[var(--conversation-row-bg-active)]",
        className,
      )}
      {...props}
    >
      {/* Active left rail (2px Aurora violet) */}
      {active && (
        <span
          aria-hidden
          className="absolute left-0 top-2 bottom-2 w-[2px] rounded-r-full"
          style={{ background: "var(--conversation-row-active-rail)" }}
        />
      )}

      {/* Avatar with channel overlay */}
      <div className="relative shrink-0">
        {c.customerAvatar ? (
          <img
            src={c.customerAvatar}
            alt={name}
            className="h-10 w-10 rounded-full object-cover border border-[var(--border-subtle)]"
            loading="lazy"
          />
        ) : (
          <div
            className="h-10 w-10 rounded-full flex items-center justify-center text-sm font-semibold text-white"
            style={{ background: "var(--accent-gradient)" }}
            aria-hidden
          >
            {initial}
          </div>
        )}
        <ChannelTypeBadge
          type={c.channelType}
          size="sm"
          iconOnly
          showLabel={false}
          className="absolute -bottom-1 -right-1 !h-[18px] !w-[18px] ring-2 ring-[var(--bg-base)]"
        />
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span
            className={cn(
              "truncate text-sm leading-tight",
              hasUnread
                ? "font-semibold text-[var(--text-primary)]"
                : "font-medium text-[var(--text-primary)]",
            )}
          >
            {name}
          </span>
          <span
            className={cn(
              "shrink-0 text-[10px] tabular-nums",
              hasUnread
                ? "font-medium text-[var(--accent-violet)]"
                : "text-[var(--text-muted)]",
            )}
          >
            {formatRelative(c.lastMessageAt ?? c.updatedAt)}
          </span>
        </div>

        <div className="mt-1 flex items-center gap-2">
          <p
            className={cn(
              "truncate text-xs flex-1",
              hasUnread
                ? "text-[var(--text-primary)]"
                : "text-[var(--text-secondary)]",
            )}
          >
            {c.lastMessageDirection === "OUTGOING" && (
              <span className="opacity-60">Siz: </span>
            )}
            {c.lastMessageSnippet ?? "..."}
          </p>
          {hasUnread && (
            <span
              className="shrink-0 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-white text-[10px] font-semibold px-1.5 tabular-nums"
              style={{
                background: "var(--conversation-unread-bg)",
                boxShadow: "var(--conversation-unread-shadow)",
              }}
              aria-label={`${c.unreadCount} o'qilmagan`}
            >
              {c.unreadCount > 99 ? "99+" : c.unreadCount}
            </span>
          )}
        </div>

        <div className="mt-1.5 flex items-center gap-1.5">
          <ConversationStateBadge status={c.status} size="sm" />
          {c.assignedUserName && (
            <span className="text-[10px] text-[var(--text-muted)] truncate">
              · {c.assignedUserName}
            </span>
          )}
        </div>
      </div>
    </button>
  );
});
ConversationListItem.displayName = "ConversationListItem";
